// Which listed models are an OLDER version of a model line that has a newer one in the same list.
// Pure and import-free, so every rule here is tested over real names without a DOM.
//
// A line is a display name with its version taken out: "Claude Sonnet 5.5" and "Claude Sonnet 4.6" are
// both the line "claude sonnet". Within a line, every version below the highest is legacy. A model whose
// line has no newer sibling is never legacy — a name with no version, or the only one of its line, is
// current by default, because "old" is only ever said relative to something newer in the same list.

/** The first number in a name ("5.5", "4", "3.8") — a version, when the name has one. */
const VERSION = /(?<![\w.])(\d+(?:\.\d+)?)(?![\w.])/;

export interface ModelLine {
  /** The name with its version removed, lowercase, single-spaced. */
  line: string;
  /** The version, or null when the name carries none. */
  version: number | null;
}

export function modelLine(name: string): ModelLine {
  const lower = name.toLowerCase().replace(/\s+/g, ' ').trim();
  const m = VERSION.exec(lower);
  if (!m) return { line: lower, version: null };
  const line = (lower.slice(0, m.index) + lower.slice(m.index + m[0].length)).replace(/\s+/g, ' ').trim();
  return { line, version: Number(m[1]) };
}

/** The (lowercase, single-spaced) names that are older versions of a line with a newer version in `names`. */
export function legacyNames(names: readonly string[]): Set<string> {
  const newest = new Map<string, number>();
  const parsed = names.map((name) => ({ key: name.toLowerCase().replace(/\s+/g, ' ').trim(), ...modelLine(name) }));
  for (const p of parsed) {
    if (p.version === null) continue;
    newest.set(p.line, Math.max(newest.get(p.line) ?? -Infinity, p.version));
  }
  const out = new Set<string>();
  for (const p of parsed) {
    if (p.version !== null && p.version < (newest.get(p.line) ?? -Infinity)) out.add(p.key);
  }
  return out;
}
